import { useState, useEffect } from 'react';
import { Table, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { getFormattedRequest } from '../services/RequestDetailsModalService';
import Loading from './Loading';

const getUserAndRole = () => {
    let userId = localStorage.getItem('userId') ? parseInt(localStorage.getItem('userId')) : 0;
    let roleId = localStorage.getItem('roleId') ? parseInt(localStorage.getItem('roleId')) : 0;
    return { userId, roleId }
};

function RequestDetailsModal({ show, handleClose, requestId }) {
    const [roleId, setRoleId] = useState(0);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (requestId && show) {
            setLoading(true);
            getFormattedRequest(requestId).then(dResult => {
                setData(dResult);
            }).catch(error => {
                console.error('Error fetching request details:', error);
                setData({});
            }).finally(() => {
                setLoading(false);
            });
        }
        let { userId, roleId } = getUserAndRole();
        setRoleId(roleId);
    }, [requestId, show]);

    const handleModalClose = () => {
        setData({});
        handleClose();
    };

    const splitReason = (reason) => {
        const raw = (reason ?? '').trim();
        if (!raw) return { concept: '', subconcept: '', detail: '' };
        const parts = raw.split('/').map(p => p.trim()).filter(Boolean);
        return { concept: parts[0] ?? '', subconcept: parts[1] ?? '', detail: parts.slice(2).join(' / ') ?? '' };
    };

    return (
        <Modal size="xl" show={show} onHide={handleModalClose} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Detalles de la Solicitud #{requestId}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <Loading />
                ) : Object.keys(data).length > 0 ? (
                    <div>
                        {/* Información General */}
                        <Row className="mb-4">
                            <Col>
                                <h5 className="mb-3">Información General</h5>
                                <Table striped bordered hover responsive>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '200px' }}><strong>ID de Solicitud</strong></td>
                                            <td>{data.id}</td>
                                            <td style={{ width: '200px' }}><strong>Estado</strong></td>
                                            <td>
                                                <Badge bg={data.statusColor || 'secondary'}>
                                                    {data.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Empleado</strong></td>
                                            <td>{data.user}</td>
                                            <td><strong>Solicitado por</strong></td>
                                            <td>{data.requester}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Período</strong></td>
                                            <td>{data.startDate} - {data.endDate}</td>
                                            <td><strong>Aprobado por</strong></td>
                                            <td>{data.approver || 'No aprobado'}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Fecha de aprobación</strong></td>
                                            <td>{data.approvalDate || 'N/A'}</td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>

                        {/* Resumen de horas y pagos - Solo para admin */}
                        {/* Items de tiempo extra */}
                        {data.items && data.items.length > 0 && (
                            <Row className="mb-4">
                                <Col>
                                    <h5 className="mb-3">Detalle de Horas Extra</h5>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Día</th>
                                                <th>Horas</th>
                                                <th>Horas a Pagar</th>
                                                <th>Concepto</th>
                                                <th>Subconcepto</th>
                                                <th>Detalle</th>
                                                <th>Trabajo desde Casa</th>
                                                {(roleId === 2 || roleId === 3) && (
                                                    <>
                                                        <th>A Pagar</th>
                                                        <th>Pago Modificado</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.items.map((item) => {
                                                const hasNormalized =
                                                    (item?.concept && String(item.concept).trim() !== '') ||
                                                    (item?.subconcept && String(item.subconcept).trim() !== '') ||
                                                    (item?.detail && String(item.detail).trim() !== '');

                                                const rr = hasNormalized ? null : splitReason(item.reason);

                                                const concept = (item.concept ?? rr?.concept ?? '').toString().trim() || '-';
                                                const subconcept = (item.subconcept ?? rr?.subconcept ?? '').toString().trim() || 'N/A';
                                                const detail = (item.detail ?? rr?.detail ?? '').toString().trim() || '-';

                                                return (
                                                    <tr key={item.id}>
                                                        <td>{item.date}</td>
                                                        <td>{item.dayOfTheWeek}</td>
                                                        <td>{item.hours}</td>
                                                        <td>{item.hoursToPay}</td>
                                                        <td>{concept}</td>
                                                        <td>{subconcept}</td>
                                                        <td>{detail}</td>
                                                        <td>
                                                            <Badge bg={item.itsWorkFromHome ? 'success' : 'secondary'}>
                                                                {item.itsWorkFromHome ? 'Sí' : 'No'}
                                                            </Badge>
                                                        </td>

                                                        {(roleId === 2 || roleId === 3) && (
                                                            <>
                                                                <td>${item.moneyToPay}</td>
                                                                <td>{item.modifiedPayment ? `$${item.modifiedPayment}` : '-'}</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        )}

                        {/* Pagos Extra */}
                        {data.formattedExtraPayments && data.formattedExtraPayments.length > 0 && (
                            <Row className="mb-4">
                                <Col>
                                    <h5 className="mb-3">Pagos Adicionales</h5>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Concepto</th>
                                                {(roleId === 2 || roleId === 3) && (
                                                    <th>Monto</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.formattedExtraPayments.map((payment) => (
                                                <tr key={payment.id}>
                                                    <td>{payment.formattedDate}</td>
                                                    <td>{payment.reason}</td>
                                                    {(roleId === 2 || roleId === 3) && (
                                                        <td>${payment.pay}</td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        )}
                        {(roleId === 2 || roleId === 3) && (
                            <Row className="mb-4">
                                <Col>
                                    <h5 className="mb-3">Resumen de Horas y Pagos</h5>
                                    <Table striped bordered hover responsive>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: '200px' }}><strong>Total de Horas</strong></td>
                                                <td>{data.hoursToPayTotal || 0} horas</td>
                                                <td style={{ width: '200px' }}><strong>Costo por Hora</strong></td>
                                                <td>${data.hourCostOT || 0}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Total a Pagar</strong></td>
                                                <td>${data.totalToPay || 0}</td>
                                                <td><strong>Total Modificado</strong></td>
                                                <td>{data.modifiedTotal ? `$${data.modifiedTotal}` : 'No modificado'}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                    {data.moneyToPayMsg && (
                                        <Alert variant="warning" className="mt-2">
                                            <strong>Nota:</strong> {data.moneyToPayMsg}
                                        </Alert>
                                    )}
                                </Col>
                            </Row>
                        )}
                    </div>
                ) : (
                    <Alert variant="danger">
                        No se pudieron cargar los detalles de la solicitud.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default RequestDetailsModal;